import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from 'chart.js';
import {
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  FiCalendar,
  FiChevronDown,
  FiClock,
  FiDownload,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { DateRange } from '~/components/Date/reactDateRange';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import MarcaBeta from '~/components/MarcaBeta';
import Tooltip from '~/components/Tooltip';
import { useAuth } from '~/hooks/useAuth';
import {
  calcularOcupacion,
  Filtros,
  horaCorta,
  LETRAS_DIAS,
  NOMBRES_DIAS,
  ResultadoSala,
} from './ocupacion';
import { useDatosOcupacion } from './useDatosOcupacion';
import SimuladorMeta from './SimuladorMeta';
import { CambioTarifa, guardarTarifas } from './tarifas';
import { useActivityLogs } from '~/hooks/useActivityLogs';
import { pesos } from '~/utils/dinero';

// Con el componente genérico `Chart` (barras + línea del promedio) hay que
// registrar también los controladores, no solo los elementos.
ChartJS.register(
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ChartTooltip,
  Legend
);

/**
 * El informe de ocupación de las salas, en una ventana a pantalla completa
 * como el calendario.
 *
 * Responde a una sola pregunta —¿cuánto se usan las salas en el horario que
 * me importa?— y deja cambiar ese horario: el mes (por defecto el anterior
 * completo), los días de la semana (por defecto lunes a viernes), las horas
 * (por defecto 6 am a 7 pm) y las salas. Todo lo demás se recalcula al
 * instante porque los datos ya están en memoria (ver `useDatosOcupacion`).
 *
 * Solo entran los espacios con hora de inicio y de fin: en una zona de día
 * completo o en una visita no hay "horas ocupadas" que medir.
 */

type Preset = 'mes-anterior' | 'este-mes' | '30-dias' | '3-meses' | '6-meses' | 'personalizado';

const PRESETS: { id: Preset; nombre: string }[] = [
  { id: 'mes-anterior', nombre: 'Mes anterior' },
  { id: 'este-mes', nombre: 'Este mes' },
  { id: '30-dias', nombre: 'Últimos 30 días' },
  { id: '3-meses', nombre: 'Últimos 3 meses' },
  { id: '6-meses', nombre: 'Últimos 6 meses' },
  { id: 'personalizado', nombre: 'Personalizado' },
];

const rangoDe = (preset: Preset, hoy = new Date()): { desde: Date; hasta: Date } => {
  switch (preset) {
    case 'este-mes':
      return { desde: startOfMonth(hoy), hasta: endOfDay(hoy) };
    case '30-dias':
      return { desde: startOfDay(subDays(hoy, 29)), hasta: endOfDay(hoy) };
    case '3-meses':
      return { desde: startOfMonth(subMonths(hoy, 2)), hasta: endOfDay(hoy) };
    case '6-meses':
      return { desde: startOfMonth(subMonths(hoy, 5)), hasta: endOfDay(hoy) };
    case 'mes-anterior':
    default: {
      const m = subMonths(hoy, 1);
      return { desde: startOfMonth(m), hasta: endOfDay(endOfMonth(m)) };
    }
  }
};

const LABORABLES = [0, 1, 2, 3, 4];
const FIN_DE_SEMANA = [5, 6];
const TODOS_LOS_DIAS = [0, 1, 2, 3, 4, 5, 6];
const HORAS_OPCIONES = Array.from({ length: 49 }, (_, i) => i * 30);

const pct = (x: number | null, decimales = 0) =>
  x === null ? '—' : `${(x * 100).toFixed(decimales)}%`;
const horas = (min: number) => {
  const h = min / 60;
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
};

const rangoTexto = (desde: Date, hasta: Date) =>
  `${format(desde, "d 'de' MMM", { locale: esLocale })} – ${format(hasta, "d 'de' MMM 'de' yyyy", {
    locale: esLocale,
  })}`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const InformeOcupacion = ({ isOpen, onClose }: Props) => {
  const { user } = useAuth();
  const companyId: string | undefined = user?.company?.id;

  const [preset, setPreset] = useState<Preset>('mes-anterior');
  const [rango, setRango] = useState(() => rangoDe('mes-anterior'));
  const [dias, setDias] = useState<number[]>(LABORABLES);
  const [horaDesde, setHoraDesde] = useState(8 * 60);
  const [horaHasta, setHoraHasta] = useState(18 * 60);
  // Efectivas por defecto: para un mes ya pasado, la ocupación real es la de
  // quien llegó (check-in o check-out). Las reservadas que nunca llegaron
  // engordarían el porcentaje sin que la sala se haya usado.
  const [soloEfectivas, setSoloEfectivas] = useState(true);
  const [salasElegidas, setSalasElegidas] = useState<string[] | null>(null);
  const [verInactivas, setVerInactivas] = useState(false);
  const [enfocada, setEnfocada] = useState<string | null>(null);
  const selectorFechas = useDisclosure();
  // Con la misma identidad mientras el rango no cambie: si se le pasa un array
  // nuevo en cada render, el calendario vuelve al mes del inicio del rango
  // cada vez que el informe se repinta (por ejemplo, al terminar de cargar).
  const rangosSelector = useMemo(
    () => [{ startDate: rango.desde, endDate: rango.hasta, key: 'sel' }],
    [rango.desde, rango.hasta]
  );
  // El simulador de meta ("¿y si llegamos al X %?").
  const simulador = useDisclosure();

  // Cada apertura arranca limpia: el mes anterior, L–V, 6 am–7 pm, todas las salas.
  useEffect(() => {
    if (!isOpen) return;
    setPreset('mes-anterior');
    setRango(rangoDe('mes-anterior'));
    setDias(LABORABLES);
    setHoraDesde(8 * 60);
    setHoraHasta(18 * 60);
    setSoloEfectivas(true);
    setSalasElegidas(null);
    setVerInactivas(false);
    setEnfocada(null);
  }, [isOpen]);

  const { salas, salasActivas, cargandoSalas, reservas, ilegibles, cargando, error, reintentar, actualizarTarifas } =
    useDatosOcupacion(companyId, rango.desde, rango.hasta, isOpen);

  const salasVisibles = verInactivas ? salas : salasActivas;
  const idsElegidos = useMemo(
    () =>
      (salasElegidas ?? salasVisibles.map((s) => s.id)).filter((id) =>
        salasVisibles.some((s) => s.id === id)
      ),
    [salasElegidas, salasVisibles]
  );

  const filtros: Filtros = useMemo(
    () => ({
      desde: rango.desde,
      hasta: rango.hasta,
      dias,
      horaDesde,
      horaHasta,
      salas: enfocada ? [enfocada] : idsElegidos,
      soloEfectivas,
      hoy: new Date(),
    }),
    [rango, dias, horaDesde, horaHasta, enfocada, idsElegidos, soloEfectivas]
  );

  const resultado = useMemo(
    () => calcularOcupacion(reservas, salas, filtros),
    [reservas, salas, filtros]
  );
  // Para el gráfico por sala siempre se comparan todas las elegidas, aunque haya una enfocada.
  const comparativa = useMemo(
    () =>
      enfocada ? calcularOcupacion(reservas, salas, { ...filtros, salas: idsElegidos }) : resultado,
    [enfocada, reservas, salas, filtros, idsElegidos, resultado]
  );

  const salaEnfocada = salas.find((s) => s.id === enfocada);
  // Reservas de las salas y días que se están mirando cuyas horas no se
  // pudieron leer (p. ej. fin antes que inicio). No se pueden contar y hay
  // que decirlo: un número que calla lo que no incluye no es de fiar.
  const ilegiblesVisibles = useMemo(() => {
    const idsSalas = new Set(enfocada ? [enfocada] : idsElegidos);
    const desdeMs = startOfDay(rango.desde).getTime();
    const hastaMs = endOfDay(rango.hasta).getTime();
    return ilegibles.filter(
      (r) =>
        idsSalas.has(r.locationId) &&
        r.dia &&
        r.dia.getTime() >= desdeMs &&
        r.dia.getTime() <= hastaMs
    );
  }, [ilegibles, enfocada, idsElegidos, rango]);
  const diasOrdenados = [...dias].sort((a, b) => a - b);
  const listo = !cargandoSalas && !cargando && !error;

  // Guardar tarifas desde el simulador: escribe lo mismo que Ajustes y deja la
  // misma huella en el historial; el informe las usa al instante.
  const { logActivity } = useActivityLogs();
  const guardarTarifasDesdeSimulador = async (cambios: CambioTarifa[]) => {
    await guardarTarifas(salas, cambios, logActivity, user?.email ?? '');
    actualizarTarifas(cambios);
  };

  const elegirPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'personalizado') setRango(rangoDe(p));
    else selectorFechas.onOpen();
  };

  const exportar = () => {
    const filasSalas = resultado.porSala.map((s) => ({
      Sala: s.sala.nombre,
      Ocupación: s.ocupacion === null ? '' : Math.round(s.ocupacion * 1000) / 10,
      'Horas reservadas': Math.round((s.ocupadoMin / 60) * 10) / 10,
      'Horas disponibles': Math.round((s.disponibleMin / 60) * 10) / 10,
      Reservas: s.reservas,
      Personas: s.personas,
      Vendido: s.vendido,
      'No vendido (tarifa)': s.noVendidoTarifa === null ? '' : Math.round(s.noVendidoTarifa),
      'Ticket por persona': s.ticketPorPersona === null ? '' : Math.round(s.ticketPorPersona),
      'Horas fuera de la ventana': Math.round((s.fueraMin / 60) * 10) / 10,
      'Precio por hora': s.sala.precioHora ?? '',
    }));
    const filasDias = resultado.porDia.map((d) => ({
      Día: d.clave,
      Ocupación: d.ocupacion === null ? '' : Math.round(d.ocupacion * 1000) / 10,
      'Horas reservadas': Math.round((d.ocupadoMin / 60) * 10) / 10,
      Reservas: d.reservas,
      'Valor vendido': d.vendido,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasSalas), 'Por sala');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasDias), 'Por día');
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        { Filtro: 'Período', Valor: rangoTexto(rango.desde, rango.hasta) },
        { Filtro: 'Días', Valor: diasOrdenados.map((d) => NOMBRES_DIAS[d]).join(', ') },
        { Filtro: 'Horas', Valor: `${horaCorta(horaDesde)} – ${horaCorta(horaHasta)}` },
        {
          Filtro: 'Reservas',
          Valor: soloEfectivas ? 'Solo efectivas (check-in / check-out)' : 'Todas las vigentes',
        },
        {
          Filtro: 'Salas',
          Valor: (enfocada
            ? [salaEnfocada?.nombre]
            : salas.filter((s) => idsElegidos.includes(s.id)).map((s) => s.nombre)
          ).join(', '),
        },
      ]),
      'Filtros'
    );
    XLSX.writeFile(
      wb,
      `ocupacion-${format(rango.desde, 'yyyy-MM-dd')}-${format(rango.hasta, 'yyyy-MM-dd')}.xlsx`
    );
  };

  /* ───────────── gráficos ───────────── */

  const conDatos = (xs: { ocupacion: number | null }[]) => xs.filter((x) => x.ocupacion !== null);
  const mejorDia = conDatos(resultado.porDia).sort(
    (a, b) => (b.ocupacion ?? 0) - (a.ocupacion ?? 0)
  )[0] as (typeof resultado.porDia)[number] | undefined;
  const peorDia = conDatos(resultado.porDia).sort(
    (a, b) => (a.ocupacion ?? 0) - (b.ocupacion ?? 0)
  )[0] as (typeof resultado.porDia)[number] | undefined;
  const horaPico = conDatos(resultado.porHora).sort(
    (a, b) => (b.ocupacion ?? 0) - (a.ocupacion ?? 0)
  )[0] as (typeof resultado.porHora)[number] | undefined;
  const horaValle = conDatos(resultado.porHora).sort(
    (a, b) => (a.ocupacion ?? 0) - (b.ocupacion ?? 0)
  )[0] as (typeof resultado.porHora)[number] | undefined;
  const media = Math.round((resultado.ocupacion ?? 0) * 1000) / 10;

  const datosDias = {
    labels: resultado.porDia.map((d) =>
      format(d.dia, 'EEE d', { locale: esLocale }).replace('.', '')
    ),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Ocupación',
        data: resultado.porDia.map((d) => Math.round((d.ocupacion ?? 0) * 1000) / 10),
        backgroundColor: resultado.porDia.map((d) =>
          d.dia.getDay() === 0 || d.dia.getDay() === 6
            ? 'rgba(124, 58, 237, .75)'
            : 'rgba(37, 99, 235, .8)'
        ),
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'line' as const,
        label: 'Promedio',
        data: resultado.porDia.map(() => media),
        borderColor: 'rgba(15, 23, 42, .45)',
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHitRadius: 0,
        order: 1,
      },
    ],
  };
  const opcionesDias = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: (item: any) => item.dataset.type !== 'line',
        callbacks: {
          title: (items: any[]) => {
            const d = resultado.porDia[items[0].dataIndex];
            return format(d.dia, "EEEE d 'de' MMMM", { locale: esLocale });
          },
          label: (ctx: any) => {
            const d = resultado.porDia[ctx.dataIndex];
            return ` ${pct(d.ocupacion, 1)} · ${horas(d.ocupadoMin)} de ${horas(
              d.disponibleMin
            )} · ${d.reservas} reservas${d.vendido ? ` · ${pesos.format(d.vendido)}` : ''}`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { callback: (v: any) => `${v}%` },
        grid: { color: 'rgba(0,0,0,.05)' },
      },
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 16 } },
    },
  };

  const datosHoras = {
    labels: resultado.porHora.map((p) => horaCorta(p.hora)),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Ocupación',
        data: resultado.porHora.map((p) => Math.round((p.ocupacion ?? 0) * 1000) / 10),
        // La hora pico se distingue del resto.
        backgroundColor: resultado.porHora.map((p) =>
          horaPico && p.hora === horaPico.hora ? 'rgba(29, 78, 216, 1)' : 'rgba(37, 99, 235, .7)'
        ),
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'line' as const,
        label: 'Promedio',
        data: resultado.porHora.map(() => media),
        borderColor: 'rgba(15, 23, 42, .45)',
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHitRadius: 0,
        order: 1,
      },
    ],
  };
  const opcionesHoras = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: (item: any) => item.dataset.type !== 'line',
        callbacks: {
          title: (items: any[]) => {
            const p = resultado.porHora[items[0].dataIndex];
            return `${horaCorta(p.hora)} – ${horaCorta(Math.min(p.hora + 60, horaHasta))}`;
          },
          label: (ctx: any) => {
            const p = resultado.porHora[ctx.dataIndex];
            return ` ${pct(p.ocupacion, 1)} · ${horas(p.ocupadoMin)} de ${horas(p.disponibleMin)}`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { callback: (v: any) => `${v}%` },
        grid: { color: 'rgba(0,0,0,.05)' },
      },
      x: { grid: { display: false }, ticks: { maxRotation: 0 } },
    },
  };

  /* ───────────── pantalla ───────────── */

  const Kpi = ({
    titulo,
    valor,
    detalle,
    testid,
  }: {
    titulo: string;
    valor: string;
    detalle?: string;
    testid?: string;
  }) => (
    <Box
      bg='white'
      borderWidth='1px'
      borderColor='gray.200'
      borderRadius='lg'
      px={4}
      py={3}
      data-testid={testid}
    >
      <Text
        fontSize='xs'
        color='gray.500'
        textTransform='uppercase'
        letterSpacing='wide'
        fontWeight='semibold'
      >
        {titulo}
      </Text>
      {/* Mientras llegan las reservas del período no se enseña ningún número:
          el anterior sería el de otro rango y parecería el de este. */}
      {cargando ? (
        <Skeleton h='26px' w='60%' mt={1.5} mb={1} borderRadius='md' />
      ) : (
        <Text fontSize='2xl' fontWeight='bold' lineHeight='1.2' mt={1}>
          {valor}
        </Text>
      )}
      {detalle && (
        <Text fontSize='xs' color='gray.500' mt={0.5}>
          {cargando ? 'cargando…' : detalle}
        </Text>
      )}
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='full' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent m={{ base: 0, md: 3 }} borderRadius={{ base: 0, md: 'lg' }} bg='gray.50'>
        <ModalHeader py={3} bg='white' borderBottomWidth='1px' borderColor='gray.200'>
          <HStack spacing={3} flexWrap='wrap'>
            <Text fontSize='lg' fontWeight='bold'>
              Ocupación de salas
            </Text>
            <MarcaBeta aviso='Informe nuevo, en etapa de evaluación. Solo cuenta los espacios con hora de inicio y de fin. Si algo no cuadra, repórtalo a Sontickets.' />
            <Text fontSize='sm' color='gray.500'>
              Espacios con hora de inicio y de fin
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} />

        <ModalBody px={{ base: 3, md: 6 }} py={4}>
          {/* ── filtros ── */}
          <Box
            bg='white'
            borderWidth='1px'
            borderColor='gray.200'
            borderRadius='lg'
            px={4}
            py={3}
            mb={4}
          >
            <Flex gap={4} wrap='wrap' align='flex-end'>
              <Box>
                <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb={1}>
                  Período
                </Text>
                <HStack spacing={2}>
                  <Select
                    size='sm'
                    w='170px'
                    borderRadius='md'
                    value={preset}
                    onChange={(e) => elegirPreset(e.target.value as Preset)}
                    data-testid='preset-periodo'
                  >
                    {PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre}
                      </option>
                    ))}
                  </Select>
                  <Popover
                    isOpen={selectorFechas.isOpen}
                    onOpen={selectorFechas.onOpen}
                    onClose={selectorFechas.onClose}
                    placement='bottom-start'
                  >
                    <PopoverTrigger>
                      <Button
                        size='sm'
                        variant='outline'
                        leftIcon={<FiCalendar />}
                        rightIcon={<FiChevronDown />}
                        fontWeight='normal'
                        data-testid='rango-fechas'
                      >
                        {rangoTexto(rango.desde, rango.hasta)}
                      </Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent w='auto'>
                        <PopoverArrow />
                        <PopoverBody p={0}>
                          <DateRange
                            locale={esLocale}
                            ranges={rangosSelector}
                            // Sin esto, al hacer el primer clic en el mes de la derecha el
                            // calendario "salta" para poner ese mes a la izquierda, y el
                            // segundo clic cae un mes más tarde de lo que se quería: así
                            // un "1 al 18 de septiembre" acababa siendo 1 sep – 18 oct.
                            preventSnapRefocus
                            onChange={(item: any) => {
                              const s = item.sel;
                              if (!s?.startDate || !s?.endDate) return;
                              setPreset('personalizado');
                              setRango({
                                desde: startOfDay(s.startDate),
                                hasta: endOfDay(s.endDate),
                              });
                            }}
                            // Sin tope: también sirve para ver cuánto hay reservado de aquí en adelante.
                            months={2}
                            direction='horizontal'
                            moveRangeOnFirstSelection={false}
                            rangeColors={['#2563eb']}
                          />
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </Popover>
                </HStack>
              </Box>

              <Box>
                <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb={1}>
                  Días
                </Text>
                <HStack spacing={2}>
                  <ButtonGroup size='sm' isAttached variant='outline'>
                    {NOMBRES_DIAS.map((nombre, i) => {
                      const activo = dias.includes(i);
                      return (
                        <Button
                          key={nombre}
                          px={2.5}
                          colorScheme={activo ? 'blue' : 'gray'}
                          variant={activo ? 'solid' : 'outline'}
                          onClick={() =>
                            setDias((prev) =>
                              prev.includes(i) ? prev.filter((d) => d !== i) : [...prev, i]
                            )
                          }
                          aria-pressed={activo}
                          aria-label={`Día ${nombre}`}
                        >
                          {LETRAS_DIAS[i]}
                        </Button>
                      );
                    })}
                  </ButtonGroup>
                  <ButtonGroup size='xs' variant='ghost' spacing={0}>
                    <Button onClick={() => setDias(LABORABLES)}>L–V</Button>
                    <Button onClick={() => setDias(FIN_DE_SEMANA)}>S–D</Button>
                    <Button onClick={() => setDias(TODOS_LOS_DIAS)}>Todos</Button>
                  </ButtonGroup>
                </HStack>
              </Box>

              <Box>
                <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb={1}>
                  Horas
                </Text>
                <HStack spacing={1}>
                  <Select
                    size='sm'
                    w='110px'
                    borderRadius='md'
                    value={horaDesde}
                    onChange={(e) => setHoraDesde(Number(e.target.value))}
                    aria-label='Desde la hora'
                  >
                    {HORAS_OPCIONES.filter((h) => h < horaHasta).map((h) => (
                      <option key={h} value={h}>
                        {horaCorta(h)}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize='sm' color='gray.500'>
                    a
                  </Text>
                  <Select
                    size='sm'
                    w='110px'
                    borderRadius='md'
                    value={horaHasta}
                    onChange={(e) => setHoraHasta(Number(e.target.value))}
                    aria-label='Hasta la hora'
                  >
                    {HORAS_OPCIONES.filter((h) => h > horaDesde).map((h) => (
                      <option key={h} value={h}>
                        {h === 1440 ? '12 am' : horaCorta(h)}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </Box>

              <Box>
                <Text fontSize='xs' fontWeight='semibold' color='gray.600' mb={1}>
                  Reservas
                </Text>
                <ButtonGroup size='sm' isAttached variant='outline'>
                  <Tooltip
                    hasArrow
                    fontSize='xs'
                    label='Las que llegaron: con check-in o check-out. Es la ocupación real de un período ya pasado.'
                  >
                    <Button
                      colorScheme={soloEfectivas ? 'blue' : 'gray'}
                      variant={soloEfectivas ? 'solid' : 'outline'}
                      onClick={() => setSoloEfectivas(true)}
                      data-testid='modo-efectivas'
                    >
                      Efectivas
                    </Button>
                  </Tooltip>
                  <Tooltip
                    hasArrow
                    fontSize='xs'
                    label='Todas menos las canceladas: incluye las pendientes y confirmadas. Útil para ver el mes en curso o lo que viene.'
                  >
                    <Button
                      colorScheme={!soloEfectivas ? 'blue' : 'gray'}
                      variant={!soloEfectivas ? 'solid' : 'outline'}
                      onClick={() => setSoloEfectivas(false)}
                      data-testid='modo-reservadas'
                    >
                      Reservadas
                    </Button>
                  </Tooltip>
                </ButtonGroup>
                <Text fontSize='10px' color='gray.400' mt={0.5}>
                  Las canceladas nunca cuentan.
                </Text>
              </Box>

              <Box flex='1' />
              <Button
                size='sm'
                colorScheme='blue'
                leftIcon={<FiTarget />}
                onClick={simulador.onOpen}
                isDisabled={!listo || resultado.disponibleMin === 0}
                data-testid='abrir-simulador'
              >
                ¿Y si llegamos al…?
              </Button>
              <Button
                size='sm'
                variant='outline'
                leftIcon={<FiDownload />}
                onClick={exportar}
                isDisabled={!listo}
              >
                Exportar
              </Button>
            </Flex>

            {/* salas */}
            <Flex mt={3} gap={2} align='center' wrap='wrap'>
              <Text fontSize='xs' fontWeight='semibold' color='gray.600' mr={1}>
                Salas
              </Text>
              {cargandoSalas ? (
                <Skeleton h='24px' w='320px' borderRadius='full' />
              ) : (
                <Wrap spacing={1.5}>
                  <WrapItem>
                    <Button
                      size='xs'
                      borderRadius='full'
                      variant={
                        !enfocada && idsElegidos.length === salasVisibles.length
                          ? 'solid'
                          : 'outline'
                      }
                      colorScheme='blue'
                      onClick={() => {
                        setSalasElegidas(null);
                        setEnfocada(null);
                      }}
                    >
                      Todas
                    </Button>
                  </WrapItem>
                  {salasVisibles.map((s) => {
                    const dentro = idsElegidos.includes(s.id);
                    const esLaEnfocada = enfocada === s.id;
                    return (
                      <WrapItem key={s.id}>
                        <Button
                          size='xs'
                          borderRadius='full'
                          variant={esLaEnfocada ? 'solid' : dentro ? 'outline' : 'ghost'}
                          colorScheme={esLaEnfocada ? 'blue' : dentro ? 'blue' : 'gray'}
                          opacity={dentro || esLaEnfocada ? 1 : 0.55}
                          onClick={() => setEnfocada((a) => (a === s.id ? null : s.id))}
                          onDoubleClick={() =>
                            setSalasElegidas((prev) => {
                              const actual = prev ?? salasVisibles.map((x) => x.id);
                              return actual.includes(s.id)
                                ? actual.filter((id) => id !== s.id)
                                : [...actual, s.id];
                            })
                          }
                          data-testid={`sala-${s.id}`}
                        >
                          {s.nombre}
                          {!s.activa && (
                            <Badge ml={1} fontSize='0.6em' colorScheme='gray'>
                              inactiva
                            </Badge>
                          )}
                        </Button>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              )}
              {salas.length > salasActivas.length && (
                <Button
                  size='xs'
                  variant='link'
                  colorScheme='gray'
                  onClick={() => setVerInactivas((v) => !v)}
                >
                  {verInactivas
                    ? 'Ocultar inactivas'
                    : `Ver ${salas.length - salasActivas.length} inactiva${
                        salas.length - salasActivas.length === 1 ? '' : 's'
                      }`}
                </Button>
              )}
              <Text fontSize='xs' color='gray.400' ml='auto'>
                Un clic: ver solo esa sala · doble clic: quitarla del total
              </Text>
            </Flex>
          </Box>

          {enfocada && salaEnfocada && (
            <HStack mb={3} spacing={2} data-testid='enfoque'>
              <Text fontSize='sm' color='gray.600'>
                Viendo solo
              </Text>
              <Badge colorScheme='blue' fontSize='0.8em' px={2} py={0.5} borderRadius='md'>
                {salaEnfocada.nombre}
              </Badge>
              <IconButton
                aria-label='Volver a todas las salas'
                icon={<FiX />}
                size='xs'
                variant='ghost'
                onClick={() => setEnfocada(null)}
              />
            </HStack>
          )}

          {error && (
            <Alert status='error' borderRadius='md' mb={4}>
              <AlertIcon />
              <AlertDescription flex='1'>{error}</AlertDescription>
              <Button
                size='sm'
                leftIcon={<FiRefreshCw />}
                onClick={reintentar}
                variant='outline'
                colorScheme='red'
              >
                Reintentar
              </Button>
            </Alert>
          )}

          {cargando && (
            <Progress size='xs' isIndeterminate colorScheme='blue' mb={3} borderRadius='full' />
          )}

          {/* ── KPIs ── */}
          <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={4}>
            <Kpi
              titulo='Ocupación'
              valor={pct(resultado.ocupacion, 1)}
              detalle={`${horas(resultado.ocupadoMin)} de ${horas(
                resultado.disponibleMin
              )} disponibles`}
              testid='kpi-ocupacion'
            />
            <Kpi
              titulo='Horas reservadas'
              valor={horas(resultado.ocupadoMin)}
              detalle={`en ${resultado.diasContados} días${
                resultado.diasFuturosFuera > 0 ? ' (hasta ayer)' : ''
              }${
                resultado.fueraMin > 0
                  ? ` · ${horas(resultado.fueraMin)} más caen fuera de ${horaCorta(horaDesde)}–${horaCorta(
                      horaHasta
                    )}`
                  : ''
              }`}
              testid='kpi-horas'
            />
            <Kpi
              titulo='Reservas'
              valor={String(resultado.reservas)}
              detalle={
                soloEfectivas
                  ? 'efectivas: con check-in o check-out'
                  : 'reservadas, sin las canceladas'
              }
              testid='kpi-reservas'
            />
            <Kpi titulo='Personas' valor={String(resultado.personas)} />
            <Kpi
              titulo='Vendido'
              valor={pesos.format(resultado.vendido)}
              detalle={
                resultado.conVenta > 0
                  ? `cobrado en ${resultado.conVenta} de ${resultado.reservas} reservas`
                  : 'ninguna reserva tiene valor anotado'
              }
              testid='kpi-vendido'
            />
            <Kpi
              titulo='No vendido (tarifa)'
              valor={resultado.noVendidoTarifa === null ? '—' : pesos.format(resultado.noVendidoTarifa)}
              detalle={
                resultado.noVendidoTarifa === null
                  ? 'ninguna sala tiene tarifa'
                  : `${horas(Math.max(0, resultado.disponibleMin - resultado.ocupadoMin))} libres × precio por hora con descuento`
              }
              testid='kpi-no-vendido'
            />
          </SimpleGrid>

          {listo && resultado.salasSinTarifa > 0 && (
            <Alert status='info' borderRadius='md' mb={4} data-testid='aviso-tarifa'>
              <AlertIcon />
              <AlertDescription fontSize='sm'>
                {resultado.salasSinTarifa === 1 ? 'Una sala no tiene' : `${resultado.salasSinTarifa} salas no tienen`} precio
                por hora configurado, así que sus horas libres no entran en «No vendido». Se configura en{' '}
                <b>Ajustes → la sede → Bloques → Tarifa</b>.
              </AlertDescription>
            </Alert>
          )}

          {listo && ilegiblesVisibles.length > 0 && (
            <Alert status='warning' borderRadius='md' mb={4} data-testid='aviso-ilegibles'>
              <AlertIcon />
              <AlertDescription fontSize='sm'>
                {ilegiblesVisibles.length === 1
                  ? 'Una reserva'
                  : `${ilegiblesVisibles.length} reservas`}{' '}
                de este período {ilegiblesVisibles.length === 1 ? 'tiene' : 'tienen'} horas que no
                se pueden leer y {ilegiblesVisibles.length === 1 ? 'no se contó' : 'no se contaron'}
                :{' '}
                {ilegiblesVisibles
                  .slice(0, 6)
                  .map(
                    (r) =>
                      `${r.code || 'sin código'} (${
                        salas.find((s) => s.id === r.locationId)?.nombre ?? 'sala'
                      }, ${r.dia ? format(r.dia, 'd MMM', { locale: esLocale }) : '?'}, ${
                        r.startHour
                      }–${r.endHour})`
                  )
                  .join(', ')}
                {ilegiblesVisibles.length > 6 ? '…' : ''}. Corrige la hora de fin desde la tabla de
                reservas y volverá a contar.
              </AlertDescription>
            </Alert>
          )}

          {listo && resultado.diasFuturosFuera > 0 && (
            <Alert status='info' borderRadius='md' mb={4} data-testid='aviso-futuro'>
              <AlertIcon />
              <AlertDescription fontSize='sm'>
                El período llega hasta el{' '}
                <b>{format(rango.hasta, "d 'de' MMMM", { locale: esLocale })}</b>, pero en{' '}
                <b>Efectivas</b> solo cuentan los días ya terminados, hasta ayer:{' '}
                <b>{resultado.diasContados}</b> de {resultado.diasContados + resultado.diasFuturosFuera}.{' '}
                {resultado.diasFuturosFuera === 1
                  ? 'Hoy no entra'
                  : `Hoy y los ${resultado.diasFuturosFuera - 1} días siguientes no entran`}{' '}
                ni como disponibles: un día a medias hundiría el porcentaje. Para ver lo reservado de hoy
                en adelante, elige{' '}
                <Button
                  size='xs'
                  variant='link'
                  colorScheme='blue'
                  onClick={() => setSoloEfectivas(false)}
                >
                  Reservadas
                </Button>
                .
              </AlertDescription>
            </Alert>
          )}

          {listo && resultado.disponibleMin === 0 && (
            <Alert status='info' borderRadius='md' mb={4}>
              <AlertIcon />
              Con estos filtros no hay ningún minuto disponible: revisa los días, las horas o las
              salas.
            </Alert>
          )}

          {/* ── detalle por sala ── */}
          <Box
            bg='white'
            borderWidth='1px'
            borderColor='gray.200'
            borderRadius='lg'
            p={4}
            mb={4}
            overflowX='auto'
            // Mientras carga, la tabla y las gráficas siguen con los datos del
            // rango anterior: se atenúan para que no se lean como los de este.
            opacity={cargando ? 0.35 : 1}
            pointerEvents={cargando ? 'none' : 'auto'}
            transition='opacity .2s'
          >
            <HStack justify='space-between' mb={3} flexWrap='wrap'>
              <Box>
                <Text fontWeight='semibold' fontSize='md'>
                  Ocupación por sala
                </Text>
                <Text fontSize='xs' color='gray.500'>
                  De más a menos usada · clic en una fila para ver solo esa sala
                </Text>
              </Box>
              {comparativa.porSala.length > 0 && comparativa.porSala[0].ocupacion !== null && (
                <HStack spacing={2} fontSize='sm' color='gray.600'>
                  <Icon as={FiTrendingUp} color='blue.500' />
                  <Text>
                    La más usada: <b>{comparativa.porSala[0].sala.nombre}</b> (
                    {pct(comparativa.porSala[0].ocupacion, 0)})
                    {comparativa.porSala.length > 1 && (
                      <>
                        {' '}
                        · la menos:{' '}
                        <b>{comparativa.porSala[comparativa.porSala.length - 1].sala.nombre}</b> (
                        {pct(comparativa.porSala[comparativa.porSala.length - 1].ocupacion, 0)})
                      </>
                    )}
                  </Text>
                </HStack>
              )}
            </HStack>
            <Table size='sm' data-testid='tabla-salas' variant='unstyled'>
              <Thead>
                <Tr borderBottomWidth='1px' borderColor='gray.200'>
                  <Th pl={2} color='gray.500' fontSize='11px'>
                    Sala
                  </Th>
                  <Th color='gray.500' fontSize='11px' w={{ base: '30%', md: '22%' }}>
                    Ocupación
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px'>
                    Horas
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px'>
                    Reservas
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px'>
                    Personas
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px'>
                    <Tooltip
                      hasArrow
                      fontSize='xs'
                      label='Lo cobrado de verdad: el valor anotado al hacer el check-out de esas reservas. Es lo mismo que suma la tabla de informes.'
                    >
                      <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                        Vendido
                      </Text>
                    </Tooltip>
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px'>
                    <Tooltip
                      hasArrow
                      fontSize='xs'
                      label='Estimación: horas libres de la sala en esta ventana × su precio por hora con el descuento (las horas libres se valoran como reservas de más de 1 hora).'
                    >
                      <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                        No vendido (tarifa)
                      </Text>
                    </Tooltip>
                  </Th>
                  <Th isNumeric color='gray.500' fontSize='11px' pr={2}>
                    <Tooltip hasArrow fontSize='xs' label='Vendido ÷ personas de las reservas con cobro.'>
                      <Text as='span' borderBottomWidth='1px' borderStyle='dotted' cursor='help'>
                        Ticket / persona
                      </Text>
                    </Tooltip>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {comparativa.porSala.map((s: ResultadoSala, i) => {
                  const oc = s.ocupacion ?? 0;
                  const tono = oc >= 0.5 ? 'blue' : oc >= 0.25 ? 'cyan' : 'gray';
                  const esta = enfocada === s.sala.id;
                  return (
                    <Tr
                      key={s.sala.id}
                      cursor='pointer'
                      bg={esta ? 'blue.50' : undefined}
                      _hover={{ bg: esta ? 'blue.50' : 'gray.50' }}
                      borderBottomWidth='1px'
                      borderColor='gray.100'
                      onClick={() => setEnfocada((a) => (a === s.sala.id ? null : s.sala.id))}
                      data-testid={`fila-sala-${s.sala.id}`}
                    >
                      <Td pl={2} py={3}>
                        <HStack spacing={3}>
                          <Flex
                            w='26px'
                            h='26px'
                            borderRadius='full'
                            bg={i === 0 ? 'blue.500' : 'gray.100'}
                            color={i === 0 ? 'white' : 'gray.600'}
                            align='center'
                            justify='center'
                            fontSize='xs'
                            fontWeight='bold'
                            flexShrink={0}
                          >
                            {i + 1}
                          </Flex>
                          <Box>
                            <HStack spacing={2}>
                              <Text fontWeight='semibold' whiteSpace='nowrap'>
                                {s.sala.nombre}
                              </Text>
                              {!s.sala.activa && (
                                <Badge fontSize='0.6em' colorScheme='gray'>
                                  inactiva
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize='xs' color='gray.500'>
                              {s.disponibleMin > 0
                                ? `${horas(s.disponibleMin)} disponibles`
                                : 'sin disponibilidad en esta ventana'}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td py={3}>
                        <HStack spacing={3}>
                          <Box
                            flex='1'
                            h='18px'
                            bg='gray.100'
                            borderRadius='full'
                            overflow='hidden'
                            position='relative'
                          >
                            <Box
                              h='100%'
                              w={`${Math.max(0, Math.round(oc * 100))}%`}
                              bg={`${tono}.500`}
                              borderRadius='full'
                              transition='width .35s ease'
                            />
                          </Box>
                          <Text fontWeight='bold' minW='52px' textAlign='right' fontSize='md'>
                            {pct(s.ocupacion, 1)}
                          </Text>
                        </HStack>
                      </Td>
                      <Td isNumeric py={3}>
                        <Text fontWeight='medium'>{horas(s.ocupadoMin)}</Text>
                      </Td>
                      <Td isNumeric py={3}>
                        {s.reservas}
                      </Td>
                      <Td isNumeric py={3}>
                        {s.personas}
                      </Td>
                      <Td isNumeric py={3}>
                        {s.vendido ? (
                          <Text fontWeight='medium'>{pesos.format(s.vendido)}</Text>
                        ) : (
                          <Text color='gray.400'>—</Text>
                        )}
                      </Td>
                      <Td isNumeric py={3}>
                        {s.noVendidoTarifa === null ? (
                          <Tooltip
                            hasArrow
                            fontSize='xs'
                            label='Sin precio por hora: configúralo en Ajustes → la sede → Bloques → Tarifa'
                          >
                            <Badge colorScheme='gray' fontSize='0.6em' cursor='help'>
                              sin tarifa
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Text color='orange.600'>{pesos.format(s.noVendidoTarifa)}</Text>
                        )}
                      </Td>
                      <Td isNumeric py={3} pr={2}>
                        {s.ticketPorPersona === null ? (
                          <Text color='gray.400'>—</Text>
                        ) : (
                          pesos.format(s.ticketPorPersona)
                        )}
                      </Td>
                    </Tr>
                  );
                })}
                {comparativa.porSala.length > 1 && (
                  <Tr bg='gray.50' fontWeight='semibold'>
                    <Td pl={2} py={3}>
                      <Text ml='38px'>Total</Text>
                    </Td>
                    <Td py={3}>
                      <HStack spacing={3}>
                        <Box flex='1' h='18px' bg='gray.200' borderRadius='full' overflow='hidden'>
                          <Box
                            h='100%'
                            w={`${Math.round((comparativa.ocupacion ?? 0) * 100)}%`}
                            bg='blue.600'
                            borderRadius='full'
                          />
                        </Box>
                        <Text fontWeight='bold' minW='52px' textAlign='right' fontSize='md'>
                          {pct(comparativa.ocupacion, 1)}
                        </Text>
                      </HStack>
                    </Td>
                    <Td isNumeric py={3}>
                      {horas(comparativa.ocupadoMin)}
                    </Td>
                    <Td isNumeric py={3}>
                      {comparativa.reservas}
                    </Td>
                    <Td isNumeric py={3}>
                      {comparativa.personas}
                    </Td>
                    <Td isNumeric py={3}>
                      {comparativa.vendido ? pesos.format(comparativa.vendido) : '—'}
                    </Td>
                    <Td isNumeric py={3} color='orange.700'>
                      {comparativa.noVendidoTarifa === null ? '—' : pesos.format(comparativa.noVendidoTarifa)}
                    </Td>
                    <Td isNumeric py={3} pr={2}>
                      {comparativa.ticketPorPersona === null
                        ? '—'
                        : pesos.format(comparativa.ticketPorPersona)}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {/* ── por día y por hora ── */}
          <SimpleGrid
            columns={{ base: 1, xl: 2 }}
            spacing={4}
            mb={4}
            opacity={cargando ? 0.35 : 1}
            transition='opacity .2s'
          >
            <Box bg='white' borderWidth='1px' borderColor='gray.200' borderRadius='lg' p={4}>
              <HStack justify='space-between' mb={1} align='flex-start' flexWrap='wrap'>
                <Box>
                  <Text fontWeight='semibold'>Por día</Text>
                  <Text fontSize='xs' color='gray.500'>
                    {enfocada && salaEnfocada ? salaEnfocada.nombre : 'Todas las salas elegidas'} ·
                    la línea es el promedio del período
                  </Text>
                </Box>
                {mejorDia && (
                  <Text fontSize='xs' color='gray.600' textAlign='right'>
                    Día más ocupado:{' '}
                    <b>{format(mejorDia.dia, "EEEE d 'de' MMM", { locale: esLocale })}</b> (
                    {pct(mejorDia.ocupacion, 0)})
                    {peorDia && peorDia !== mejorDia && (
                      <>
                        <br />
                        Menos: <b>
                          {format(peorDia.dia, "EEEE d 'de' MMM", { locale: esLocale })}
                        </b>{' '}
                        ({pct(peorDia.ocupacion, 0)})
                      </>
                    )}
                  </Text>
                )}
              </HStack>
              <HStack spacing={3} fontSize='xs' color='gray.500' mb={2}>
                <HStack spacing={1}>
                  <Box w='10px' h='10px' bg='rgba(37, 99, 235, .8)' borderRadius='2px' />
                  <Text>entre semana</Text>
                </HStack>
                <HStack spacing={1}>
                  <Box w='10px' h='10px' bg='rgba(124, 58, 237, .75)' borderRadius='2px' />
                  <Text>fin de semana</Text>
                </HStack>
              </HStack>
              <Box h='260px' data-testid='grafico-dias'>
                {resultado.porDia.length > 0 && (
                  <Chart type='bar' data={datosDias} options={opcionesDias} />
                )}
              </Box>
            </Box>
            <Box bg='white' borderWidth='1px' borderColor='gray.200' borderRadius='lg' p={4}>
              <HStack justify='space-between' mb={1} align='flex-start' flexWrap='wrap'>
                <Box>
                  <Text fontWeight='semibold'>Por hora del día</Text>
                  <Text fontSize='xs' color='gray.500'>
                    Promedio de los días elegidos · la línea es el promedio de la ventana
                  </Text>
                </Box>
                {horaPico && (
                  <Text fontSize='xs' color='gray.600' textAlign='right'>
                    Hora pico:{' '}
                    <b>
                      {horaCorta(horaPico.hora)}–
                      {horaCorta(Math.min(horaPico.hora + 60, horaHasta))}
                    </b>{' '}
                    ({pct(horaPico.ocupacion, 0)})
                    {horaValle && horaValle !== horaPico && (
                      <>
                        <br />
                        Más floja:{' '}
                        <b>
                          {horaCorta(horaValle.hora)}–
                          {horaCorta(Math.min(horaValle.hora + 60, horaHasta))}
                        </b>{' '}
                        ({pct(horaValle.ocupacion, 0)})
                      </>
                    )}
                  </Text>
                )}
              </HStack>
              <Box h='260px' mt={6} data-testid='grafico-horas'>
                {resultado.porHora.length > 0 && (
                  <Chart type='bar' data={datosHoras} options={opcionesHoras} />
                )}
              </Box>
            </Box>
          </SimpleGrid>

          <HStack spacing={4} fontSize='xs' color='gray.500' px={1} flexWrap='wrap'>
            <HStack spacing={1}>
              <Icon as={FiClock} />
              <Text>
                Ocupación = horas reservadas ÷ horas disponibles en {horaCorta(horaDesde)}–
                {horaCorta(horaHasta)} los {diasOrdenados.map((d) => NOMBRES_DIAS[d]).join(', ')},
                recortadas al horario de cada sala.
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon as={FiUsers} />
              <Text>
                Las horas de una reserva cuentan solo por la parte que cae dentro de esa ventana; su valor, sus
                personas y la propia reserva cuentan enteros si la tocan.
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon as={FiTrendingUp} />
              <Text>
                «Vendido» es lo cobrado de verdad: lo anotado en el check-out, lo mismo que suma la tabla de
                informes. «No vendido (tarifa)» es una estimación: las horas libres de cada sala × su{' '}
                <b>precio por hora con el descuento</b> de más de 1 hora (Ajustes → la sede → Bloques → Tarifa, o
                el botón de precios del simulador): las horas libres se valoran como se vende casi todo, en
                reservas de más de una hora.
              </Text>
            </HStack>
          </HStack>
        </ModalBody>

        {/* Dentro del contenido, no al lado: ver el comentario equivalente en CalendarioReservas. */}
        <SimuladorMeta
          isOpen={simulador.isOpen}
          onClose={simulador.onClose}
          // Sobre lo que se está viendo: si hay una sala enfocada, solo esa.
          resultado={resultado}
          onGuardarTarifas={guardarTarifasDesdeSimulador}
          descripcionFiltros={`${rangoTexto(rango.desde, rango.hasta)} · ${diasOrdenados
            .map((d) => NOMBRES_DIAS[d])
            .join(', ')} · ${horaCorta(horaDesde)}–${horaCorta(horaHasta)} · ${
            soloEfectivas ? 'efectivas' : 'reservadas'
          } · ${
            enfocada && salaEnfocada ? `solo ${salaEnfocada.nombre}` : `${idsElegidos.length} salas`
          }`}
        />
      </ModalContent>
    </Modal>
  );
};

export default InformeOcupacion;
